/**
 * ScrollRestoration - Restaure la position de scroll lors des navigations "Retour/Suivant".
 *
 * Clé = location.pathname (robuste face à l'instabilité de location.key en BrowserRouter).
 * - Navigation PUSH (nouvelle page) → remonte en haut.
 * - Navigation POP (Retour/Suivant) → restaure la position sauvegardée pour ce chemin.
 * - Navigation REPLACE (synchro de filtres sur la même page) → on ne touche pas au scroll.
 *
 * IMPORTANT : la sauvegarde se fait via useLayoutEffect pour détacher le listener AVANT
 * le paint de la nouvelle route. Sinon, quand la nouvelle page (plus courte) se rend, le
 * navigateur "clamp" window.scrollY à 0 et déclenche un event scroll qui écraserait la
 * position sauvegardée de la page qu'on quitte.
 *
 * Le contenu défile sur window (cf. DashboardLayout), donc window.scrollY suffit.
 */

import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

const PREFIX = 'scrollpos:';

// Désactiver la restauration native du navigateur (on la gère nous-mêmes).
if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
    try { window.history.scrollRestoration = 'manual'; } catch { /* ignore */ }
}

export default function ScrollRestoration() {
    const location = useLocation();
    const navType = useNavigationType(); // 'POP' | 'PUSH' | 'REPLACE'
    const pathRef = useRef(location.pathname);
    // Mémorise le pathname réellement affiché lors de la dernière navigation traitée,
    // pour distinguer un vrai changement de page d'un simple changement de query params
    // (ex. drill-down apporteur/VP, filtres) sur la MÊME page.
    const lastHandledPathRef = useRef(location.pathname);

    // Sauvegarde continue (layout effect : cleanup synchrone avant paint de la route suivante).
    useLayoutEffect(() => {
        pathRef.current = location.pathname;
        const save = () => {
            try { sessionStorage.setItem(PREFIX + pathRef.current, String(window.scrollY)); } catch { /* quota */ }
        };
        window.addEventListener('scroll', save, { passive: true });
        return () => {
            save(); // capture la position courante AVANT le paint de la nouvelle route
            window.removeEventListener('scroll', save);
        };
    }, [location.pathname]);

    // Restauration (POP) ou remontée (PUSH). REPLACE = même page → ne rien faire.
    useEffect(() => {
        const samePage = location.pathname === lastHandledPathRef.current;
        lastHandledPathRef.current = location.pathname;

        if (navType === 'PUSH') {
            // On ne remonte en haut que pour un VRAI changement de page.
            // Un PUSH qui ne change que les query params (même pathname : drill-down,
            // filtres, sélection de scope sur le dashboard…) conserve la position de scroll
            // → pas de saut visuel disgracieux.
            if (!samePage) window.scrollTo(0, 0);
            return;
        }
        if (navType !== 'POP') return;

        let saved = 0;
        try { saved = parseInt(sessionStorage.getItem(PREFIX + location.pathname) || '0', 10) || 0; } catch { /* ignore */ }
        if (saved <= 0) {
            window.scrollTo(0, 0);
            return;
        }

        // Le contenu (listes, fiches) se charge de façon asynchrone : on réessaie de
        // restaurer la position tant que la page n'est pas assez haute, jusqu'à un délai max.
        // Un ResizeObserver réapplique aussi la position quand la hauteur du document change.
        let cancelled = false;
        const start = Date.now();
        const MAX_MS = 6000;
        const apply = () => {
            const maxScrollable = document.documentElement.scrollHeight - window.innerHeight;
            window.scrollTo(0, Math.min(saved, Math.max(0, maxScrollable)));
            return Math.abs(window.scrollY - saved) <= 4;
        };
        const tryRestore = () => {
            if (cancelled) return;
            if (apply()) return;
            if (Date.now() - start < MAX_MS) requestAnimationFrame(tryRestore);
        };
        requestAnimationFrame(tryRestore);
        let ro = null;
        try {
            ro = new ResizeObserver(() => {
                if (cancelled) return;
                if (apply() || Date.now() - start >= MAX_MS) { if (ro) ro.disconnect(); }
            });
            ro.observe(document.documentElement);
        } catch { /* pas de ResizeObserver */ }
        return () => { cancelled = true; if (ro) ro.disconnect(); };
    }, [location.pathname, location.key, navType]);

    return null;
}
